import { selectDiscounts } from '@/redux/data/dataSlice';
import { DiscountType } from '@/redux/data/dataTypes';
import { useAppSelector } from '@/redux/hooks';
import { InboxOutlined } from '@ant-design/icons';
import Fuse from 'fuse.js';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DiscountItem from './DiscountItem';

interface DiscountListProps {
  searchTxt?: string;
  listContentClassName?: string;
  onItemPress?: (item: DiscountType) => void;
}

const DiscountList: FC<DiscountListProps> = ({ searchTxt, listContentClassName, onItemPress }) => {
  const { t } = useTranslation();
  const discounts = useAppSelector(selectDiscounts);

  const filteredList = useMemo(() => {
    if (searchTxt && searchTxt.length >= 3) {
      const fuse = new Fuse(discounts, {
        includeScore: false,
        isCaseSensitive: false,
        threshold: 0.3,
        keys: ['name'],
      });
      return fuse.search(searchTxt).map((d) => d.item);
    }
    return discounts;
  }, [discounts, searchTxt]);

  if (filteredList.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-full p-8 bg-[#f6f8fc] dark:bg-gray-700 rounded-xl">
        <div className="text-center p-8 rounded-xl bg-white dark:bg-gray-600 shadow-sm">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 flex items-center justify-center bg-[#e6f7ff] dark:bg-gray-500 text-frenchBlue dark:text-white rounded-full">
              <InboxOutlined className="text-2xl" />
            </div>
          </div>
          <div className="text-lg font-semibold text-gray-800 dark:text-white mb-1">
            {t('common:no_discounts_found')}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-300">Try adjusting your search</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#f6f8fc] dark:bg-gray-700 rounded-xl p-1 scrollable">
      <div
        className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 ${listContentClassName}`}
      >
        {filteredList.map((discount) => (
          <DiscountItem key={discount.ID} discount={discount} onItemPress={onItemPress} />
        ))}
      </div>
    </div>
  );
};

export default DiscountList;
